import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Briefcase, Users, Brain, AlertCircle, TrendingUp, Clock } from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await api.get('/admin/dashboard');
      setStats(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!stats) return (
    <div className="p-8 text-center text-gray-500">Failed to load dashboard data.</div>
  );

  const metricCards = [
    { label: 'Total Cases', value: stats.total_cases, icon: Briefcase, color: 'bg-black' },
    { label: 'New (24h)', value: stats.new_cases_24h, icon: TrendingUp, color: 'bg-primary' },
    { label: 'Unassigned', value: stats.unassigned_cases, icon: AlertCircle, color: 'bg-destructive' },
    { label: 'Templates', value: stats.template_count, icon: Brain, color: 'bg-success' },
    { label: 'Pending Review', value: stats.pending_candidates, icon: Clock, color: 'bg-ai-indicator' },
    { label: 'Users', value: Object.values(stats.user_counts || {}).reduce((a, b) => a + b, 0), icon: Users, color: 'bg-gray-700' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Operations overview</p>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 border border-gray-200" data-testid="dashboard-metrics">
        {metricCards.map(({ label, value, icon: Icon, color }, i) => (
          <div
            key={label}
            className={`p-6 ${i < metricCards.length - 1 ? 'border-r border-b' : 'border-b'} border-gray-200 ${i % 3 === 2 ? 'border-r-0' : ''}`}
            data-testid={`metric-${label.toLowerCase().replace(/[\s()]/g, '-')}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-6 h-6 ${color} rounded-sm flex items-center justify-center`}>
                <Icon className="w-3 h-3 text-white" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{label}</span>
            </div>
            <p className="font-heading text-3xl font-bold">{value}</p>
          </div>
        ))}
      </div>

      {/* Case Status Breakdown */}
      <div className="mt-8 border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-heading text-lg font-semibold">Cases by Status</h3>
        </div>
        <div className="grid grid-cols-4 divide-x divide-gray-200">
          {Object.entries(stats.case_counts || {}).map(([status, count]) => (
            <div key={status} className="p-4 text-center">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">{status.replace(/_/g, ' ')}</span>
              <span className="font-heading text-2xl font-bold">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* User Breakdown */}
      <div className="mt-8 border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-heading text-lg font-semibold">Users by Role</h3>
        </div>
        <div className="grid grid-cols-4 divide-x divide-gray-200">
          {Object.entries(stats.user_counts || {}).map(([role, count]) => (
            <div key={role} className="p-4 text-center">
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400 block mb-1">{role}</span>
              <span className="font-heading text-2xl font-bold">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
